/**
 * Service Google Analytics et Google Tag Manager
 * Gestion du tracking avec respect du consentement RGPD
 */

// Configuration des IDs (à modifier avec vos vrais IDs)
const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || process.env.REACT_APP_GTAG_ID || 'G-XXXXXXXXXX';
const GTM_ID = process.env.REACT_APP_GTM_ID || 'GTM-XXXXXXX';
const FORCE_DEBUG_MODE = process.env.REACT_APP_ENV === 'development'; // Force le chargement en dev

class GoogleAnalyticsService {
  constructor() {
    this.isInitialized = false;
    this.gtmLoaded = false;
    this.gaLoaded = false;
    this.debugMode = process.env.REACT_APP_ENV === 'development';
  }
  /**
   * Initialise Google Analytics et Google Tag Manager
   * Ne se lance que si le consentement cookies est accordé
   */
  initialize() {
    const consent = localStorage.getItem('cookieConsent');
    
    if (consent !== 'accepted') {
      if (this.debugMode) console.log('🍪 Analytics désactivé - Pas de consentement cookies');
      return;
    }

    if (this.isInitialized) {
      if (this.debugMode) console.log('📊 Analytics déjà initialisé');
      return;
    }    // Vérifier que les IDs sont configurés
    if (!FORCE_DEBUG_MODE && (!GA_MEASUREMENT_ID || GA_MEASUREMENT_ID === 'G-XXXXXXXXXX')) {
      console.warn('⚠️ GA_MEASUREMENT_ID non configuré');
      return;
    }

    if (!FORCE_DEBUG_MODE && (!GTM_ID || GTM_ID === 'GTM-XXXXXXX')) {
      console.warn('⚠️ GTM_ID non configuré');
      return;
    }

    // En mode debug, on force l'initialisation même avec des IDs de test
    if (FORCE_DEBUG_MODE) {
      console.log('🧪 Mode debug activé - Chargement forcé avec IDs de test');
    }

    this.initializeGTM();
    this.initializeGA();
    this.isInitialized = true;
    
    if (this.debugMode) console.log('📊 Google Analytics & GTM initialisés avec:', {
      GA_ID: GA_MEASUREMENT_ID,
      GTM_ID: GTM_ID
    });
  }  /**
   * Initialise Google Tag Manager
   */
  initializeGTM() {
    if (this.gtmLoaded) return;

    // En mode debug, simuler GTM même avec des IDs de test
    if (FORCE_DEBUG_MODE && (!GTM_ID || GTM_ID === 'GTM-XXXXXXX' || GTM_ID === 'GTM-M9QBL7TW')) {
      console.log('🧪 Simulation GTM en mode debug');
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        'gtm.start': new Date().getTime(),
        event: 'gtm.js',
        debug_mode: true
      });
      this.gtmLoaded = true;
      return;
    }

    try {
      // Initialiser DataLayer AVANT le script GTM
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        'gtm.start': new Date().getTime(),
        event: 'gtm.js'
      });

      // GTM Script dans le head
      const gtmScript = document.createElement('script');
      gtmScript.async = true;
      gtmScript.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
      gtmScript.onload = () => {
        this.gtmLoaded = true;
        if (this.debugMode) console.log('✅ GTM Script chargé');
      };
      gtmScript.onerror = () => {
        console.error('❌ Erreur chargement GTM');
      };
      document.head.appendChild(gtmScript);

      // NoScript fallback
      const gtmNoScript = document.createElement('noscript');
      gtmNoScript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${GTM_ID}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
      document.body.insertBefore(gtmNoScript, document.body.firstChild);

      if (this.debugMode) console.log('🏷️ GTM initialisé avec ID:', GTM_ID);
      
    } catch (error) {
      // Erreur silencieuse
      if (this.debugMode) console.log('ℹ️ GTM non disponible:', error.message);
    }
  }  /**
   * Initialise Google Analytics 4
   */
  initializeGA() {
    if (this.gaLoaded) return;

    // En mode debug, simuler GA4 même avec des IDs de test
    if (FORCE_DEBUG_MODE && (!GA_MEASUREMENT_ID || GA_MEASUREMENT_ID === 'G-XXXXXXXXXX' || GA_MEASUREMENT_ID === 'G-NKMZB3FHZK')) {
      console.log('🧪 Simulation GA4 en mode debug');
      window.dataLayer = window.dataLayer || [];
      function gtag() { window.dataLayer.push(arguments); }
      window.gtag = gtag;
      
      gtag('js', new Date());
      gtag('config', 'DEBUG-GA4', {
        page_title: document.title,
        page_location: window.location.href,
        debug_mode: true
      });
      
      this.gaLoaded = true;
      return;
    }

    try {
      // Chargement du script GA4
      const gaScript = document.createElement('script');
      gaScript.async = true;
      gaScript.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
      gaScript.onload = () => {
        this.gaLoaded = true;
        if (this.debugMode) console.log('✅ GA4 Script chargé');
      };
      gaScript.onerror = () => {
        // Erreur silencieuse - probablement bloqueur de publicités
        // On initialise un fallback pour ne pas bloquer l'application
        this.initializeGAFallback();
        if (this.debugMode) console.log('ℹ️ GA4 non disponible (bloqueur de publicités détecté)');
      };
      document.head.appendChild(gaScript);

      // Configuration GA4
      window.dataLayer = window.dataLayer || [];
      function gtag() { window.dataLayer.push(arguments); }
      window.gtag = gtag;

      gtag('js', new Date());
      gtag('config', GA_MEASUREMENT_ID, {
        page_title: document.title,
        page_location: window.location.href,
        debug_mode: this.debugMode
      });

      if (this.debugMode) console.log('📈 GA4 initialisé avec ID:', GA_MEASUREMENT_ID);

    } catch (error) {
      // Erreur silencieuse - initialiser le fallback
      this.initializeGAFallback();
      if (this.debugMode) console.log('ℹ️ GA4 non disponible:', error.message);
    }
  }

  /**
   * Initialise un fallback GA4 en cas de blocage
   * Permet à l'application de continuer à fonctionner normalement
   */
  initializeGAFallback() {
    // Créer des fonctions vides pour éviter les erreurs
    window.dataLayer = window.dataLayer || [];
    if (!window.gtag) {
      window.gtag = function() {
        // Fonction vide - ne fait rien mais ne cause pas d'erreur
        if (this.debugMode) {
          console.log('📊 GA4 Fallback:', arguments);
        }
      }.bind(this);
    }
    this.gaLoaded = false; // Marquer comme non chargé
    if (this.debugMode) console.log('🔄 Fallback GA4 activé');
  }

  /**
   * Track une page vue
   */
  trackPageView(pagePath, pageTitle) {
    if (!this.isInitialized) return;

    // Via GTM DataLayer
    window.dataLayer?.push({
      event: 'page_view',
      page_path: pagePath,
      page_title: pageTitle,
      page_location: window.location.href
    });

    // Via GA4 direct
    window.gtag?.('config', GA_MEASUREMENT_ID, {
      page_path: pagePath,
      page_title: pageTitle
    });
  }

  /**
   * Track un événement personnalisé
   */
  trackEvent(eventName, parameters = {}) {
    if (!this.isInitialized) return;

    // Via GTM DataLayer
    window.dataLayer?.push({
      event: eventName,
      ...parameters
    });

    // Via GA4 direct
    window.gtag?.('event', eventName, parameters);
  }

  /**
   * Track une conversion
   */
  trackConversion(conversionName, value = null, currency = 'EUR') {
    const params = {
      event_category: 'conversion',
      event_label: conversionName
    };

    if (value) {
      params.value = value;
      params.currency = currency;
    }

    this.trackEvent('conversion', params);
  }

  /**
   * Track une interaction utilisateur
   */
  trackUserInteraction(action, category = 'user_interaction', label = null) {
    this.trackEvent('user_interaction', {
      event_category: category,
      event_action: action,
      event_label: label
    });
  }

  /**
   * Track un clic sur bouton
   */
  trackButtonClick(buttonName, location = null) {
    this.trackUserInteraction('button_click', 'engagement', `${buttonName}${location ? `_${location}` : ''}`);
  }

  /**
   * Track un téléchargement
   */
  trackDownload(fileName, fileType = 'unknown') {
    this.trackEvent('file_download', {
      event_category: 'download',
      event_action: 'download',
      event_label: fileName,
      file_type: fileType
    });
  }

  /**
   * Track un clic sur lien externe
   */
  trackExternalLink(url, linkText = null) {
    this.trackEvent('external_link', {
      event_category: 'outbound',
      event_action: 'click',
      event_label: linkText || url,
      external_url: url
    });
  }

  /**
   * Désactive le tracking et nettoie les cookies
   */
  disable() {
    this.isInitialized = false;
    this.gtmLoaded = false;
    this.gaLoaded = false;

    // Suppression des cookies Google Analytics
    this.deleteCookie('_ga');
    this.deleteCookie('_ga_' + GA_MEASUREMENT_ID.replace('G-', ''));
    this.deleteCookie('_gid');
    this.deleteCookie('_gat');

    console.log('📊 Analytics désactivé et cookies supprimés');
  }

  /**
   * Supprime un cookie
   */
  deleteCookie(name) {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${window.location.hostname}`;
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
  }

  /**
   * Vérifie si le tracking est actif
   */
  isTrackingEnabled() {
    return this.isInitialized && localStorage.getItem('cookieConsent') === 'accepted';
  }
}

// Instance singleton
const googleAnalytics = new GoogleAnalyticsService();

export default googleAnalytics;
